'use client';

import React from 'react';

import { SectionHeader } from '@layout';
import type { SectionHeaderProps } from '@layout';

type SectionHeaderLinkProps = Pick<
  SectionHeaderProps,
  | 'headingLinkText'
  | 'headingLinkHref'
  | 'headingLinkTarget'
  | 'headingLinkRel'
  | 'headingLinkAs'
  | 'headingLinkClass'
  | 'headingLinkStyle'
  | 'headingLinkOnClick'
  | 'headingLinkDisabled'
  | 'headingLinkPosition'
  | 'buttonLinkText'
  | 'buttonLinkHref'
  | 'buttonLinkTarget'
  | 'buttonLinkRel'
  | 'buttonLinkAs'
  | 'buttonLinkClass'
  | 'buttonLinkIcon'
  | 'buttonLinkIconPosition'
>;

interface SectionGroupProps extends SectionHeaderLinkProps {
  /** Stable anchor for deep links, e.g. `group-actions` → `#group-actions` */
  groupId: string;
  title: string;
  description?: string;
  /** Optional line above the title (e.g. framing a demo as real product usage). */
  eyebrow?: string;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  /** Extra classes on the group’s `SectionHeader` `<header>` (merged with `section-header`). */
  headerClassName?: string;
  /** Classes on the outer group wrapper (e.g. featured workflow band). */
  groupClassName?: string;
  /** Hint text to display below the title (none if omitted). */
  headingHint?: string;
  headingClassName?: string;
  descriptionClassName?: string;
  children: React.ReactNode;
}

/**
 * Wraps a functional group of design system demos with a shared title, one-line intent,
 * and consistent spacing. Individual demos keep their own `sectionID` anchors.
 */
const SectionGroup: React.FC<SectionGroupProps> = ({
  groupId,
  title,
  headingHint,
  description,
  eyebrow,
  as = 'h2',
  headerClassName,
  groupClassName,
  headingClassName,
  descriptionClassName,
  headingLinkText,
  headingLinkHref,
  headingLinkTarget,
  headingLinkRel,
  headingLinkAs,
  headingLinkClass,
  headingLinkStyle,
  headingLinkOnClick,
  headingLinkDisabled,
  headingLinkPosition,
  buttonLinkText,
  buttonLinkHref,
  buttonLinkTarget,
  buttonLinkRel,
  buttonLinkAs,
  buttonLinkClass,
  buttonLinkIcon,
  buttonLinkIconPosition,
  children,
}) => {
  const groupClasses = [groupClassName].filter(Boolean).join(' ');
  return (
    <div id={groupId} className={groupClasses}>
      <SectionHeader
        headingHint={headingHint}
        title={title}
        description={description}
        as={as}
        eyebrow={eyebrow}
        headerClassName={headerClassName}
        headingClassName={headingClassName}
        descriptionClassName={descriptionClassName}
        headingLinkText={headingLinkText}
        headingLinkHref={headingLinkHref}
        headingLinkTarget={headingLinkTarget}
        headingLinkRel={headingLinkRel}
        headingLinkAs={headingLinkAs}
        headingLinkClass={headingLinkClass}
        headingLinkStyle={headingLinkStyle}
        headingLinkOnClick={headingLinkOnClick}
        headingLinkDisabled={headingLinkDisabled}
        headingLinkPosition={headingLinkPosition}
        buttonLinkText={buttonLinkText}
        buttonLinkHref={buttonLinkHref}
        buttonLinkTarget={buttonLinkTarget}
        buttonLinkRel={buttonLinkRel}
        buttonLinkAs={buttonLinkAs}
        buttonLinkClass={buttonLinkClass}
        buttonLinkIcon={buttonLinkIcon}
        buttonLinkIconPosition={buttonLinkIconPosition}
      />
      {children}
    </div>
  );
};

export type { SectionGroupProps };
export default SectionGroup;
